/**
 * The fridge sheet: Family Fuel on paper, for a family that would rather
 * tally the fun budget by hand than open the app for it.
 *
 * `Sheet` is pure presentation — plain props in, markup out — so both the
 * hosted app and trial mode can render the identical page from their own
 * shapes of state. Neither caller hands it anything live: once it's on the
 * fridge, the household is the one keeping it in sync, not this component.
 */

import { Link } from 'react-router-dom';
import { useFamily } from '../lib/family.jsx';
import { useFuel } from '../lib/fuel.js';
import { formatCents } from '../domain/money.js';
import { TileIcon } from '../ui/Art.jsx';
import '../styles/print.css';

const CADENCE_PHRASE = {
  weekly: 'every week',
  biweekly: 'every two weeks',
  monthly: 'every month',
};

const PERIOD_NOUN = {
  weekly: 'week',
  biweekly: 'two weeks',
  monthly: 'month',
};

const TALLY_ROWS = 14;

export function Sheet({ familyName, kids, rule, tiles, goal, balanceCents }) {
  const cadencePhrase = CADENCE_PHRASE[rule?.cadence] ?? CADENCE_PHRASE.weekly;
  const periodNoun = PERIOD_NOUN[rule?.cadence] ?? PERIOD_NOUN.weekly;
  const activeTiles = (tiles ?? []).filter((t) => !t.archivedAt);

  return (
    <div className="fridge-sheet stack">
      <div className="fridge-sheet__head">
        <div>
          <p className="eyebrow">{familyName}</p>
          <h1 className="fridge-sheet__title">Family Fuel</h1>
        </div>
        <p className="fridge-sheet__week-line">Week of ________________</p>
      </div>

      <p className="fridge-sheet__meta">
        {formatCents(rule?.amountCents ?? 0)} {cadencePhrase}
        {rule?.rolloverCapCents > 0 && <> · up to {formatCents(rule.rolloverCapCents)} carries over</>}
        {goal && <> · saving for {goal.name}{goal.targetCents ? ` (${formatCents(goal.targetCents)})` : ''}</>}
        {kids?.length > 0 && <> · {kids.map((k) => k.display_name ?? k.displayName ?? k.name).join(', ')}</>}
      </p>

      {activeTiles.length > 0 && (
        <>
          <h2>What things usually cost</h2>
          <table className="fridge-sheet__prices">
            <tbody>
              {activeTiles.map((tile) => (
                <tr key={tile.id}>
                  <td style={{ width: '1.6em' }}><TileIcon tile={tile} /></td>
                  <td>{tile.label}</td>
                  <td>{formatCents(tile.defaultAmountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h2>Keeping tally</h2>
      <table className="fridge-sheet__tally">
        <thead>
          <tr>
            <th>Date</th>
            <th>What</th>
            <th>Who</th>
            <th>Cost</th>
            <th>Left</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: TALLY_ROWS }).map((_, i) => (
            <tr key={i}>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td className={i === 0 ? 'fridge-sheet__prefilled' : undefined}>
                {/* The period starts full — this is the refill amount, not
                    whatever a live balance happens to read when it's printed. */}
                {i === 0 ? formatCents(balanceCents) : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="fridge-sheet__footer">
        {goal
          ? `Left over goes to ${goal.name}.`
          : `Left over rolls into next ${periodNoun}.`}
      </p>
    </div>
  );
}

/** Hosted wrapper: pulls the same data `FamilyHome` reads and hands it to `Sheet`. */
export default function FridgeSheet() {
  const { family, kids } = useFamily();
  const fuel = useFuel(family?.id);

  if (fuel.loading) return <main className="app-shell"><p className="muted">Loading…</p></main>;

  return (
    <main className="app-shell stack">
      <div className="fridge-sheet__actions">
        <Link to="/family"><button className="quiet">Done</button></Link>
        <button className="primary" onClick={() => window.print()}>Print</button>
      </div>
      {fuel.envelope ? (
        <Sheet
          familyName={family.name}
          kids={kids}
          rule={fuel.rule}
          tiles={fuel.tiles}
          goal={fuel.goal}
          balanceCents={fuel.rule?.amountCents ?? 0}
        />
      ) : (
        <p className="muted">Set up Family Fuel first, then there'll be something to print.</p>
      )}
    </main>
  );
}
